import { useEffect, useState } from 'react'

/**
 * `useMediaQuery('(min-width: 1100px)')` → true while the query matches.
 *
 * Use it to mount ONE layout instead of rendering phone and desktop trees
 * and hiding one with CSS (which doubled every request, toast and DOM id —
 * UX-143). Reads synchronously on first render so there's no flash of the
 * wrong layout.
 */
export function useMediaQuery(query: string): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false

  const [matches, setMatches] = useState<boolean>(get)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    // Safari < 14
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [query])

  return matches
}
